import { z } from 'zod'

/**
 * Server-side environment variables schema
 */
const runtime = z.object({
  // Core Configuration
  SITE_URL: z.string().url(),
  PORT: z.string().optional().default('3000'),
  HOSTNAME: z.string().optional().default('localhost'),
  CORS_ORIGIN: z.string().default('http://localhost:3000'),
  REACT_EDITOR: z.string().optional(),
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),

  // Development Settings
  FORCE_EMAIL_SEND: z.string().or(z.boolean()).optional().default('false'),

  // Admin Access
  ADMIN_EMAIL: z.string().email(),

  // Authentication
  AUTH_SECRET: z.string().min(32),
  AUTH_URL: z.string().url(),
  DEFAULT_LOGIN_REDIRECT: z.string().default('/account'),

  // OAuth Providers
  AUTH_GOOGLE_ID: z.string(),
  AUTH_GOOGLE_SECRET: z.string(),
  AUTH_FACEBOOK_ID: z.string().optional().default(''),
  AUTH_FACEBOOK_SECRET: z.string().optional().default(''),

  // Security & Encryption
  CSRF_SECRET: z.string().min(32),
  ENCRYPTION_KEY: z.string(),

  // Database Configuration
  DATABASE_URL: z.string(),
  POSTGRES_DB: z.string(),
  POSTGRES_USER: z.string(),
  POSTGRES_PASSWORD: z.string(),
  DATABASE_POOL_SIZE: z.string().or(z.number()).default('10'),
  DATABASE_POOL_MIN: z.string().or(z.number()).default('2'),
  DATABASE_CONNECTION_TIMEOUT: z.string().or(z.number()).default('30000'),
  DATABASE_ACQUIRE_TIMEOUT: z.string().or(z.number()).default('60000'),
  DATABASE_IDLE_TIMEOUT: z.string().or(z.number()).default('600000'),

  // Redis Configuration
  REDIS_HOST: z.string(),
  REDIS_PORT: z.string(),
  REDIS_PASSWORD: z.string(),
  REDIS_URL: z.string().optional().default('redis://:${REDIS_PASSWORD}@redis:6379'),
  REDIS_DB: z.string().optional().default('0'),
  REDIS_MEMORY_LIMIT: z.string().optional(),
  REDIS_DISABLE_CACHE_CLEARING: z.string().or(z.boolean()).optional(),
  REDIS_RATE_LIMIT_IN_DEV: z.string().or(z.boolean()).optional().default('true'),
  REDIS_CONNECT_TIMEOUT: z.string().or(z.number()).default('10000'),
  REDIS_COMMAND_TIMEOUT: z.string().or(z.number()).default('5000'),
  REDIS_MAX_RETRY_DELAY: z.string().or(z.number()).default('2000'),
  REDIS_FAILOVER_DELAY: z.string().or(z.number()).default('100'),
  REDIS_MAX_RETRIES: z.string().or(z.number()).default('3'),
  REDIS_POOL_MIN: z.string().or(z.number()).default('2'),
  REDIS_POOL_MAX: z.string().or(z.number()).default('10'),

  // AWS Configuration
  // AWS_* is the SES credential pair. S3_* is the media-bucket pair and is
  // preferred by getS3Credentials(); AWS_* remains only as a fallback for
  // environments not yet migrated. Keep them separate: a leaked media key must
  // not be able to send email as the company, and vice versa.
  AWS_ACCESS_KEY_ID: z.string(),
  AWS_SECRET_ACCESS_KEY: z.string(),
  AWS_REGION: z.string(),
  AWS_BUCKET_NAME: z.string(),
  S3_ACCESS_KEY_ID: z.string().optional(),
  S3_SECRET_ACCESS_KEY: z.string().optional(),

  // Email Configuration
  MAIL_FROM: z.string().email(),
  MAIL_TO: z.string().email().optional(),
  MAIL_CC: z.string().email().optional(),
  MAIL_BCC: z.string().email().optional(),
  MAIL_REPLY_TO: z.string().email().optional(),
  SES_SMTP_USER: z.string().optional(),
  SES_SMTP_PASSWORD: z.string().optional(),
  SES_SMTP_HOST: z.string().optional(),
  SES_SMTP_PORT: z.string().optional(),
  SES_SMTP_FROM: z.string().optional(),

  // Bambora Configuration
  BAMBORA_MERCHANT_ID: z.string(),
  BAMBORA_ORDER_API_ACCESS_PASSCODE: z.string(),
  BAMBORA_REPORTING_API_ACCESS_PASSCODE: z.string(),
  BAMBORA_PAYMENT_PROFILE_API_ACCESS_PASSCODE: z.string(),
  BAMBORA_HASH_KEY: z.string(),
  BAMBORA_HASH_ALGORITHM: z.string(),
  BAMBORA_API_BASE_URL: z.string(),

  // Socket Configuration
  SOCKET_PATH: z.string().default('/api/socket'),
  SOCKET_PORT: z.string().default('3001'),

  // Media Service Configuration
  MEDIA_SERVICE_URL: z.string().url(),
  MEDIA_SERVICE_PORT: z.string().default('3310'),
  MEDIA_SERVICE_ALLOWED_ORIGINS: z.string().url(),

  // Third-Party API Keys
  ABSTRACT_API_KEY: z.string(),
  ABSTRACT_API_GEOLOCATION_KEY: z.string(),
  MEASUREMENT_PROTOCOL_API_SECRET: z.string(),

  // Twilio Configuration
  TWILIO_ACCOUNT_SID: z.string(),
  TWILIO_AUTH_TOKEN: z.string(),
  TWILIO_PHONE_NUMBER: z.string(),
  TWILIO_VERIFY_SERVICE_SID: z.string(),

  // Web Push (VAPID) — optional; push is a graceful no-op when unset.
  VAPID_PRIVATE_KEY: z.string().optional().default(''),
  VAPID_SUBJECT: z.string().optional().default('mailto:admin@choice.graphics'),

  // Cron Settings
  CAPACITY_QUEUE_CLEANUP_CRON: z.string().default('0 3 * * *'),

  // Worker Configuration
  WORKER_CONCURRENCY: z.string().or(z.number()).default('2'),
  WORKER_MEMORY_LIMIT: z.string().default('512M'),
  WORKER_CPU_LIMIT: z.string().default('1.0'),
  WORKER_MEMORY_RESERVATION: z.string().default('256M'),
  WORKER_CPU_RESERVATION: z.string().default('0.5'),
  WORKER_MAX_STALLED: z.string().or(z.number()).default('1'),
  WORKER_STALLED_INTERVAL: z.string().or(z.number()).default('30000'),
  WORKER_REMOVE_ON_COMPLETE: z.string().or(z.number()).default('10'),
  WORKER_REMOVE_ON_FAIL: z.string().or(z.number()).default('50'),

  // Queue Configuration
  QUEUE_JOB_ATTEMPTS: z.string().or(z.number()).default('3'),
  QUEUE_BACKOFF_DELAY: z.string().or(z.number()).default('2000'),
  QUEUE_REMOVE_ON_COMPLETE: z.string().or(z.number()).default('50'),
  QUEUE_REMOVE_ON_FAIL: z.string().or(z.number()).default('50'),
  QUEUE_JOB_TIMEOUT: z.string().or(z.number()).default('300000'),
  QUEUE_STALLED_INTERVAL: z.string().or(z.number()).default('30000'),
  QUEUE_MAX_STALLED_COUNT: z.string().or(z.number()).default('1'),

  // Monitoring Configuration
  LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
  NEXT_PUBLIC_LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
  GRAFANA_ADMIN_PASSWORD: z.string().min(8)
})

/**
 * Client-side environment variables schema (only NEXT_PUBLIC_ variables)
 */
const buildtime = z.object({
  // Public URLs
  NEXT_PUBLIC_API_URL: z.string().url(),
  NEXT_PUBLIC_APP_URL: z.string().url(),
  NEXT_PUBLIC_DASHBOARD_URL: z.string().url(),
  NEXT_PUBLIC_MEDIA_CDN: z.string().url(),

  // Socket Configuration
  NEXT_PUBLIC_SOCKET_URL: z.string().url(),
  NEXT_PUBLIC_SHOW_SOCKET_DEBUG: z.string().or(z.boolean()).optional(),

  // Third-Party Integrations
  NEXT_PUBLIC_GTM_ID: z.string().optional(),
  NEXT_PUBLIC_COOKIE_CONSENT_KEY: z.string(),

  // Web Push public key (base64url) — empty disables the opt-in UI.
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: z.string().optional().default(''),

})

/**
 * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
 * middlewares) or client-side so we need to destruct manually.
 *
 * @type {Record<keyof z.infer<typeof runtime> | keyof z.infer<typeof buildtime>, string | undefined>}
 */
const processEnv = {
  // Core Configuration
  SITE_URL: process.env.SITE_URL,
  PORT: process.env.PORT,
  HOSTNAME: process.env.HOSTNAME,
  CORS_ORIGIN: process.env.CORS_ORIGIN,
  REACT_EDITOR: process.env.REACT_EDITOR,

  // Development Settings
  FORCE_EMAIL_SEND: process.env.FORCE_EMAIL_SEND,

  // Admin Access
  ADMIN_EMAIL: process.env.ADMIN_EMAIL,

  // Authentication
  AUTH_SECRET: process.env.AUTH_SECRET,
  AUTH_URL: process.env.AUTH_URL,
  DEFAULT_LOGIN_REDIRECT: process.env.DEFAULT_LOGIN_REDIRECT,

  // OAuth Providers
  AUTH_GOOGLE_ID: process.env.AUTH_GOOGLE_ID,
  AUTH_GOOGLE_SECRET: process.env.AUTH_GOOGLE_SECRET,
  AUTH_FACEBOOK_ID: process.env.AUTH_FACEBOOK_ID,
  AUTH_FACEBOOK_SECRET: process.env.AUTH_FACEBOOK_SECRET,

  // Security & Encryption
  CSRF_SECRET: process.env.CSRF_SECRET,
  ENCRYPTION_KEY: process.env.ENCRYPTION_KEY,

  // Bambora Configuration
  BAMBORA_MERCHANT_ID: process.env.BAMBORA_MERCHANT_ID,
  BAMBORA_ORDER_API_ACCESS_PASSCODE: process.env.BAMBORA_ORDER_API_ACCESS_PASSCODE,
  BAMBORA_REPORTING_API_ACCESS_PASSCODE: process.env.BAMBORA_REPORTING_API_ACCESS_PASSCODE,
  BAMBORA_PAYMENT_PROFILE_API_ACCESS_PASSCODE: process.env.BAMBORA_PAYMENT_PROFILE_API_ACCESS_PASSCODE,
  BAMBORA_HASH_KEY: process.env.BAMBORA_HASH_KEY,
  BAMBORA_HASH_ALGORITHM: process.env.BAMBORA_HASH_ALGORITHM,
  BAMBORA_API_BASE_URL: process.env.BAMBORA_API_BASE_URL,

  // Database Configuration
  DATABASE_URL: process.env.DATABASE_URL,
  POSTGRES_DB: process.env.POSTGRES_DB,
  POSTGRES_USER: process.env.POSTGRES_USER,
  POSTGRES_PASSWORD: process.env.POSTGRES_PASSWORD,

  // Redis Configuration
  REDIS_HOST: process.env.REDIS_HOST,
  REDIS_PORT: process.env.REDIS_PORT,
  REDIS_PASSWORD: process.env.REDIS_PASSWORD,
  REDIS_URL: process.env.REDIS_URL,
  REDIS_DB: process.env.REDIS_DB,
  REDIS_MEMORY_LIMIT: process.env.REDIS_MEMORY_LIMIT,
  REDIS_DISABLE_CACHE_CLEARING: process.env.REDIS_DISABLE_CACHE_CLEARING,
  REDIS_RATE_LIMIT_IN_DEV: process.env.REDIS_RATE_LIMIT_IN_DEV,

  // AWS Configuration
  AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
  AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
  AWS_REGION: process.env.AWS_REGION,
  AWS_BUCKET_NAME: process.env.AWS_BUCKET_NAME,
  S3_ACCESS_KEY_ID: process.env.S3_ACCESS_KEY_ID,
  S3_SECRET_ACCESS_KEY: process.env.S3_SECRET_ACCESS_KEY,

  // Email Configuration
  MAIL_FROM: process.env.MAIL_FROM,
  MAIL_TO: process.env.MAIL_TO,
  MAIL_CC: process.env.MAIL_CC,
  MAIL_BCC: process.env.MAIL_BCC,
  MAIL_REPLY_TO: process.env.MAIL_REPLY_TO,
  SES_SMTP_USER: process.env.SES_SMTP_USER,
  SES_SMTP_PASSWORD: process.env.SES_SMTP_PASSWORD,
  SES_SMTP_HOST: process.env.SES_SMTP_HOST,
  SES_SMTP_PORT: process.env.SES_SMTP_PORT,
  SES_SMTP_FROM: process.env.SES_SMTP_FROM,

  // Socket Configuration
  SOCKET_PATH: process.env.SOCKET_PATH,
  SOCKET_PORT: process.env.SOCKET_PORT,
  NEXT_PUBLIC_SOCKET_URL: process.env.NEXT_PUBLIC_SOCKET_URL,
  NEXT_PUBLIC_SOCKET_PATH: process.env.NEXT_PUBLIC_SOCKET_PATH,
  NEXT_PUBLIC_SHOW_SOCKET_DEBUG: process.env.NEXT_PUBLIC_SHOW_SOCKET_DEBUG,

  // Public URLs
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
  NEXT_PUBLIC_DASHBOARD_URL: process.env.NEXT_PUBLIC_DASHBOARD_URL,
  NEXT_PUBLIC_MEDIA_CDN: process.env.NEXT_PUBLIC_MEDIA_CDN,

  // Third-Party API Keys
  ABSTRACT_API_KEY: process.env.ABSTRACT_API_KEY,
  ABSTRACT_API_GEOLOCATION_KEY: process.env.ABSTRACT_API_GEOLOCATION_KEY,
  MEASUREMENT_PROTOCOL_API_SECRET: process.env.MEASUREMENT_PROTOCOL_API_SECRET,
  NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
  NEXT_PUBLIC_COOKIE_CONSENT_KEY: process.env.NEXT_PUBLIC_COOKIE_CONSENT_KEY,

  // Twilio Configuration
  TWILIO_ACCOUNT_SID: process.env.TWILIO_ACCOUNT_SID,
  TWILIO_AUTH_TOKEN: process.env.TWILIO_AUTH_TOKEN,
  TWILIO_PHONE_NUMBER: process.env.TWILIO_PHONE_NUMBER,
  TWILIO_VERIFY_SERVICE_SID: process.env.TWILIO_VERIFY_SERVICE_SID,

  // Web Push (VAPID)
  VAPID_PRIVATE_KEY: process.env.VAPID_PRIVATE_KEY,
  VAPID_SUBJECT: process.env.VAPID_SUBJECT,
  NEXT_PUBLIC_VAPID_PUBLIC_KEY: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,

  // Cron Settings
  CAPACITY_QUEUE_CLEANUP_CRON: process.env.CAPACITY_QUEUE_CLEANUP_CRON,

  // Worker Configuration
  WORKER_CONCURRENCY: process.env.WORKER_CONCURRENCY,
  WORKER_MEMORY_LIMIT: process.env.WORKER_MEMORY_LIMIT,
  WORKER_CPU_LIMIT: process.env.WORKER_CPU_LIMIT,
  WORKER_MEMORY_RESERVATION: process.env.WORKER_MEMORY_RESERVATION,
  WORKER_CPU_RESERVATION: process.env.WORKER_CPU_RESERVATION,
  WORKER_MAX_STALLED: process.env.WORKER_MAX_STALLED,
  WORKER_STALLED_INTERVAL: process.env.WORKER_STALLED_INTERVAL,
  WORKER_REMOVE_ON_COMPLETE: process.env.WORKER_REMOVE_ON_COMPLETE,
  WORKER_REMOVE_ON_FAIL: process.env.WORKER_REMOVE_ON_FAIL,

  // Queue Configuration
  QUEUE_JOB_ATTEMPTS: process.env.QUEUE_JOB_ATTEMPTS,
  QUEUE_BACKOFF_DELAY: process.env.QUEUE_BACKOFF_DELAY,
  QUEUE_REMOVE_ON_COMPLETE: process.env.QUEUE_REMOVE_ON_COMPLETE,
  QUEUE_REMOVE_ON_FAIL: process.env.QUEUE_REMOVE_ON_FAIL,
  QUEUE_JOB_TIMEOUT: process.env.QUEUE_JOB_TIMEOUT,
  QUEUE_STALLED_INTERVAL: process.env.QUEUE_STALLED_INTERVAL,
  QUEUE_MAX_STALLED_COUNT: process.env.QUEUE_MAX_STALLED_COUNT,

  // Monitoring Configuration
  LOG_LEVEL: process.env.LOG_LEVEL,
  NEXT_PUBLIC_LOG_LEVEL: process.env.NEXT_PUBLIC_LOG_LEVEL,
  GRAFANA_ADMIN_PASSWORD: process.env.GRAFANA_ADMIN_PASSWORD,

  // Media Service Configuration
  MEDIA_SERVICE_URL: process.env.MEDIA_SERVICE_URL,
  MEDIA_SERVICE_PORT: process.env.MEDIA_SERVICE_PORT,
  MEDIA_SERVICE_ALLOWED_ORIGINS: process.env.MEDIA_SERVICE_ALLOWED_ORIGINS
}

const merged = runtime.merge(buildtime)

/** @typedef {z.input<typeof merged>} MergedInput */
/** @typedef {z.infer<typeof merged>} MergedOutput */
/** @typedef {z.SafeParseReturnType<MergedInput, MergedOutput>} MergedSafeParseReturn */

// @ts-expect-error - process.env is typed as NodeJS.ProcessEnv but we need to cast it to our merged schema
let env = /** @type {MergedOutput} */ (process.env)

if (!!process.env.SKIP_ENV_VALIDATION == false) {
  const isServer = typeof window === 'undefined'

  const parsed = /** @type {MergedSafeParseReturn} */ (
    isServer // on server we can validate all env vars
      ? merged.safeParse(processEnv)
      : buildtime.safeParse(processEnv) // on client we can only validate the ones that are exposed
  )

  if (parsed.success === false) {
    console.error('❌ Invalid environment variables:', parsed.error.flatten().fieldErrors)
    throw new Error('Invalid environment variables')
  }

  env = new Proxy(parsed.data, {
    get(target, prop) {
      if (typeof prop !== 'string') return undefined
      // Only guard props that are real env keys in the schema. Dev tooling
      // probes arbitrary props on every export (Turbopack's React Refresh
      // registration reads 'constructor'), and throwing on those crashes
      // every page in `next dev`.
      if (!isServer && !prop.startsWith('NEXT_PUBLIC_') && !Object.hasOwn(merged.shape, prop))
        return undefined
      // Throw a descriptive error if a server-side env var is accessed on the client
      // Otherwise it would just be returning `undefined` and be annoying to debug
      if (!isServer && !prop.startsWith('NEXT_PUBLIC_'))
        throw new Error(
          process.env.NODE_ENV === 'production'
            ? '❌ Attempted to access a server-side environment variable on the client'
            : `❌ Attempted to access server-side environment variable '${prop}' on the client. If this is a server action, ensure you've added 'use server' at the top of your file.`
        )
      return target[/** @type {keyof typeof target} */ (prop)]
    }
  })
}

export { env }
